import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { User, UserIcon, Settings, BarChart, Sparkles, CreditCard, CheckCircle, ArrowDown, Ban, Zap } from 'lucide-react'; // Added Zap
import { SubscriptionButton } from '@/components/SubscriptionButton';
import { issueBillingKey } from '@/lib/payment/portone';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useCardInfo, useSubscriptionPlans, useBillingRetry } from '@/lib/payment/hooks';
import { SubscriptionPlan } from '@/lib/payment/types';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { isUserCancelError } from '@/lib/payment/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { SubscriptionManageDialog } from '@/components/SubscriptionManageDialog';
import { supabase } from '@/supabase/client';
import { PaymentService } from '@/supabase/payment/paymentService';
import { logError } from '@/lib/sentry';
import { getActiveReferralEvent } from '@/supabase/referral/repository/referralEventRepository';
import { formatEventLastDay, refereeRewardLabel, type ReferralEventState } from '@/lib/referralEvent';
import { MessageSquare, Star } from 'lucide-react';
import { FeatureSettingsSection } from '@/components/FeatureSettingsSection';

// Custom progress component to show usage stats
interface UsageProgressProps {
  value: number;
  max: number | 'unlimited';
  label: string; // Added label for different usage types
}

function UsageProgress({ value, max, label }: UsageProgressProps) {
  const percentage = max === 'unlimited' ? 0 : Math.min(100, Math.round((value / (max as number)) * 100));
  
  return (
    <div className="w-full space-y-2">
      <div className="flex justify-between text-sm text-gray-500">
        <span>{label}: {value}/{max === 'unlimited' ? '무제한' : max}</span>
        <span>{max === 'unlimited' ? '' : `${percentage}%`}</span>
      </div>
      <Progress value={percentage} className="h-2" />
    </div>
  );
}

// Subscription Plan interface
interface Plan {
  id: string;
  name: string;
  price: number;
  originalPrice?: number; // 추가: 원가
  discountRate?: number; // 추가: 할인율 (예: 50 for 50%)
  limit: number | 'unlimited';
  copyLimit: number | 'unlimited'; // Added for copy usage
  features: string[];
  color: string;
  recommended?: boolean;
  description?: string;
  icon?: React.ReactNode; // Added icon
}

// Plan Card Component
interface PlanCardProps {
  plan: Plan;
  currentPlan?: string;
  isReferralDiscount?: boolean; // 피추천인 첫 결제 90% 할인 여부
  referralRewardText?: string | null; // 피추천인 추가 혜택 문구 (null = 미표시)
}

function PlanCard({ plan, currentPlan, isReferralDiscount, referralRewardText }: PlanCardProps) {
  const { user } = useAuth();
  const isCurrentPlan = plan.id === currentPlan;
  
  // 플랜별 배경색 설정
  const getBgColor = (planId: string) => {
    switch (planId) {
      case 'standard': return 'bg-blue-50';
      case 'premium': return 'bg-purple-50';
      default: return 'bg-white';
    }
  };

  // 플랜별 테두리 색상과 스타일 설정
  const getBorderStyle = (planId: string, isCurrentPlan: boolean) => {
    if (isCurrentPlan) {
      switch (planId) {
        case 'standard': return 'border-blue-500 border-2';
        case 'premium': return 'border-purple-500 border-2';
        default: return 'border-gray-300';
      }
    }
    return plan.id === 'standard'
        ? 'border-blue-300'
        : 'border-purple-300';
  };

  // 플랜별 그림자 효과 설정
  const getShadowStyle = (isCurrentPlan: boolean) => {
    return isCurrentPlan ? 'shadow-lg' : '';
  };

  // 플랜별 텍스트 색상
  const getTextColor = (planId: string) => {
    switch (planId) {
      case 'standard': return 'text-blue-800';
      case 'premium': return 'text-purple-800';
      default: return 'text-gray-800';
    }
  };

  // 플랜별 배지 배경색
  const getBadgeBgColor = (planId: string) => {
    switch (planId) {
      case 'standard': return 'bg-blue-200';
      case 'premium': return 'bg-purple-200';
      default: return 'bg-gray-200';
    }
  };
  
  // 플랜별 현재 이용 중 배지 스타일
  const getCurrentPlanBadgeStyle = (planId: string) => {
    switch (planId) {
      case 'standard': return 'bg-blue-700 text-white';
      case 'premium': return 'bg-purple-700 text-white';
      default: return 'bg-gray-700 text-white';
    }
  };

  const dailyPrice = plan.price > 0 ? Math.round(plan.price / 30) : 0;
  const discountedPrice = Math.round(plan.price * 0.1); // 90% 할인 가격

  return (
    <Card
      className={`flex flex-col h-full ${getBorderStyle(plan.id, isCurrentPlan)} ${getBgColor(plan.id)} ${getShadowStyle(isCurrentPlan)} transition-all duration-300 hover:transform hover:scale-[1.02] hover:shadow-md ${isCurrentPlan ? 'relative' : ''} rounded-lg overflow-hidden p-6`}
    >
      {isCurrentPlan && (
        <div className={`${getCurrentPlanBadgeStyle(plan.id)} text-sm font-medium px-4 py-1.5 absolute top-0 right-0 left-0 z-10`}>
          ✓ 현재 이용 중인 플랜
        </div>
      )}
      {/* 피추천인 첫 결제 할인 배지 */}
      {isReferralDiscount && plan.price > 0 && (
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold px-3 py-1.5 absolute top-0 right-0 left-0 z-10 text-center">
          🎁 친구 초대 특별 할인 - 첫 달 90% OFF!
        </div>
      )}
      <CardHeader className={`p-0 pb-4 space-y-2 items-center text-center ${isCurrentPlan || (isReferralDiscount && plan.price > 0) ? 'mt-10' : ''}`}>
        {plan.icon && (
          <div className="flex items-center justify-center mb-3">
            {plan.icon}
          </div>
        )}
        <div className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-sm font-medium ${getBadgeBgColor(plan.id)} ${getTextColor(plan.id)} mx-auto`}>
          {plan.name}
        </div>
        <CardTitle className="mt-2">
          {/* 피추천인 할인 가격 표시 */}
          {isReferralDiscount && plan.price > 0 ? (
            <div>
              <div className="flex items-center justify-center mb-0.5">
                <span className="text-xs sm:text-sm text-gray-500 line-through">
                  ₩{plan.price.toLocaleString()}
                </span>
                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold bg-purple-100 text-purple-700">
                  90% 할인
                </span>
              </div>
              <div className="flex items-baseline justify-center">
                <span className="text-2xl sm:text-3xl font-bold text-purple-600">
                  ₩{discountedPrice.toLocaleString()}
                </span>
                <span className="text-xs sm:text-sm text-gray-500 font-normal ml-1">/첫 달</span>
              </div>
              <div className="text-center text-xs text-gray-500 mt-0.5">
                (하루에 약 <span className="font-bold text-purple-600">{Math.round(discountedPrice / 30).toLocaleString()}원</span>)
              </div>
              {referralRewardText && (
                <p className="text-xs text-purple-600 text-center mt-2">
                  {referralRewardText}
                </p>
              )}
            </div>
          ) : plan.originalPrice && plan.discountRate ? (
            <div>
              <div className="flex items-center justify-center mb-0.5">
                <span className="text-xs sm:text-sm text-gray-500 line-through">
                  ₩{plan.originalPrice.toLocaleString()}
                </span>
                <span className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold ${
                  plan.id === 'standard' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                }`}>
                  {plan.discountRate}% 할인
                </span>
              </div>
              <div className="flex items-baseline justify-center">
                <span className={`text-2xl sm:text-3xl font-bold ${getTextColor(plan.id)}`}>
                  ₩{(plan.price || 0).toLocaleString()}
                </span>
                <span className="text-xs sm:text-sm text-gray-500 font-normal ml-1">/월</span>
              </div>
              {dailyPrice > 0 && (
                <div className="text-center text-xs text-gray-500 mt-0.5">
                  (하루에 약 <span className={`font-bold ${plan.id === 'standard' ? 'text-blue-600' : 'text-purple-600'}`}>{dailyPrice.toLocaleString()}원</span>)
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="flex items-baseline justify-center">
                <span className={`text-2xl sm:text-3xl font-bold ${getTextColor(plan.id)}`}>
                  ₩{(plan.price || 0).toLocaleString()}
                </span>
                <span className="text-xs sm:text-sm text-gray-500 font-normal ml-1">/월</span>
              </div>
              {dailyPrice > 0 && (
                <div className="text-center text-xs text-gray-500 mt-0.5">
                  (하루에 약 <span className={`font-bold ${plan.id === 'standard' ? 'text-blue-600' : 'text-purple-600'}`}>{dailyPrice.toLocaleString()}원</span>)
                </div>
              )}
            </div>
          )}
        </CardTitle>
        {plan.description && (
          <p className="text-xs sm:text-sm text-gray-500 h-8 sm:h-10 mt-2 text-center">{plan.description}</p>
        )}
      </CardHeader>
      <CardContent className="flex-grow p-0 pt-2">
        <div className={`w-16 h-1 mb-4 mx-auto ${
          plan.id === 'standard'
              ? 'bg-blue-400'
              : 'bg-purple-400'
        }`}></div>
        <ul className="space-y-2">
          {plan.features?.map((feature, index) => (
            <li key={index} className="flex items-start">
              <CheckCircle className={`h-4 w-4 mr-2 ${
                plan.id === 'standard'
                    ? 'text-blue-500'
                    : 'text-purple-500'
              } mt-0.5 flex-shrink-0`} />
              <span className="text-xs sm:text-sm text-gray-700 leading-relaxed">{feature}</span>
            </li>
          ))}
        </ul>
      </CardContent>
      <CardFooter className="pt-6 pb-6"> {/* Adjusted pt-6 from pt-8 */}
        {user ? (
          <SubscriptionButton
            userId={user.id}
            planId={plan.id}
            fullWidth
            variant={plan.id === 'standard' ? 'primary' : 'primary'}
            disabled={isCurrentPlan}
          />
        ) : (
          <Button
            className={`w-full ${
              plan.id === 'standard'
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-purple-600 hover:bg-purple-700 text-white'
            }`}
            onClick={() => toast.error('로그인이 필요합니다.')}
          >
            로그인하여 구독하기
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}

// 스켈레톤 컴포넌트들
function UserProfileSkeleton() {
  return (
    <div className="flex flex-col md:flex-row items-center gap-6">
      <Skeleton className="w-24 h-24 rounded-full" />
      <div className="space-y-2">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-32" />
      </div>
    </div>
  );
}

function PlanCardSkeleton() {
  return (
    <Card className="flex flex-col h-full border rounded-lg">
      <CardHeader className="pb-4 pt-6 space-y-4">
        <Skeleton className="h-6 w-24" />
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-4 w-48" />
      </CardHeader>
      <CardContent className="flex-grow pt-2">
        <Skeleton className="w-16 h-1 mb-6" />
        <div className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
        </div>
      </CardContent>
      <CardFooter className="pt-8 pb-6">
        <Skeleton className="h-10 w-full" />
      </CardFooter>
    </Card>
  );
}

function UsageStatsSkeleton() {
  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-2 w-full" />
      </div>
      <div className="bg-gray-50 p-4 rounded-lg space-y-2">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-4 w-48" />
      </div>
    </div>
  );
}

function CardInfoSkeleton() {
  return (
    <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-5">
      <div className="flex flex-col md:flex-row items-center">
        <Skeleton className="w-64 h-40 rounded-xl mb-4 md:mb-0 md:mr-6" />
        <div className="space-y-4">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-9 w-24" />
        </div>
      </div>
    </div>
  );
}

// Testimonial Card Component
interface Testimonial {
  name: string;
  avatarText: string;
  review: string;
}

interface TestimonialCardProps {
  testimonial: Testimonial;
}

function TestimonialCard({ testimonial }: TestimonialCardProps) {
  return (
    <Card className="bg-white shadow-lg rounded-xl overflow-hidden transform transition-all hover:scale-105">
      <CardContent className="p-6">
        <div className="flex items-start space-x-4">
          <Avatar className="w-12 h-12 border-2 border-gray-200">
            <AvatarFallback className="text-lg bg-gray-100 text-gray-600">
              {testimonial.avatarText}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="text-sm font-semibold text-gray-800">{testimonial.name}</p>
            <div className="flex items-center mt-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 text-yellow-400 fill-yellow-400" />
              ))}
            </div>
          </div>
        </div>
        <blockquote className="mt-4 text-gray-600 italic leading-relaxed">
          "{testimonial.review}"
        </blockquote>
      </CardContent>
    </Card>
  );
}

// Testimonials Section Component
function TestimonialsSection() {
  const testimonials: Testimonial[] = [
    {
      name: '익명 셀러 A',
      avatarText: 'A',
      review: "매출에서 몇퍼센트씩 떼어가겠다 이런 곳들은 많은데, 솔직히 그런 곳들은 G*** 하나로 충분하거든요. 원래 하던 라방에 집중할 수 있게 해줘서 좋아요."
    },
    {
      name: '익명 셀러 B',
      avatarText: 'B',
      review: "저는 이제 이 사이트 없으면 정산서 정리 못해요😂 어느정도 느낌이냐면 다른 셀러분들은 몰랐으면 좋겠을 정도예요 ㅎㅎ"
    },
    {
      name: '익명 셀러 C',
      avatarText: 'C',
      review: "셀러헬퍼 쓰기 전보다 효율이 훨씬 좋아졌습니다! 방송할 때마다 1시간씩은 세이브되는 것 같아요!"
    }
  ];

  return (
    <div className="border rounded-lg overflow-hidden bg-white shadow-sm"> {/* 다른 패널과 동일한 스타일 적용 */}
      <div className="border-b px-6 py-4 flex items-center"> {/* 패널 헤더 */}
        <MessageSquare className="h-5 w-5 mr-2 text-teal-500" />
        <h2 className="text-xl font-semibold text-gray-900">실제 셀러님들의 생생한 후기</h2>
      </div>
      <div className="p-6 space-y-6"> {/* 패널 컨텐츠 영역 */}
        <p className="text-sm text-gray-600 text-center"> {/* 설명 문구 추가 */}
          셀러헬퍼를 통해 업무 효율을 높이고 성장을 경험한 셀러님들의 이야기를 들어보세요.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6"> {/* 카드 간격을 gap-6으로 조정 */}
          {testimonials.map((testimonial, index) => (
            <TestimonialCard key={index} testimonial={testimonial} />
          ))}
        </div>
      </div>
    </div>
  );
}


export default function MyPage() {
  const { user } = useAuth();
  const { subscriptionPlan, subscription, usageStat, isLoading, refreshSubscription, isPastDue } = useSubscription();
  
  // 플랜 정보 훅 사용
  const { plans: fetchedPlans, isLoading: plansLoading } = useSubscriptionPlans();
  
  // 커스텀 훅 사용
  const { cardInfo, isLoading: cardInfoLoading, refreshCardInfo } = useCardInfo(
    user?.id, 
    subscriptionPlan?.id || null
  );

  // 연체 재결제 훅 — 등록된 빌링키로 카드창 없이 즉시 재결제
  // 배너 버튼(B)과 카드 변경 후 자동 재결제(A)가 같은 인스턴스를 공유해
  // 한쪽 진행 중이면 다른 쪽 버튼도 비활성화된다.
  const { retryBilling, isRetrying } = useBillingRetry();

  const [plans, setPlans] = useState<Plan[]>([]);
  const [usageStats, setUsageStats] = useState({
    used: 0,
    total: 150 as number | 'unlimited',
    periodStart: new Date(),
    periodEnd: new Date(new Date().setMonth(new Date().getMonth() + 1)),
  });
  const [copyUsageStats, setCopyUsageStats] = useState({
    used: 0,
    total: 'unlimited' as number | 'unlimited', // Default to unlimited
    periodStart: new Date(),
    periodEnd: new Date(new Date().setMonth(new Date().getMonth() + 1))
  });
  
  // 카드 로딩 상태 추가
  const [cardLoading, setCardLoading] = useState(false);
  
  // 구독 관리 다이얼로그 상태
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);

  // 피추천인 첫 결제 90% 할인 여부
  const [isReferralDiscount, setIsReferralDiscount] = useState(false);
  // 피추천인 추가 혜택 문구 (null = 미표시)
  const [referralRewardText, setReferralRewardText] = useState<string | null>(null);

  // 플랜 정보 설정
  useEffect(() => {
    if (fetchedPlans && fetchedPlans.length > 0) {
      const formattedPlans: Plan[] = fetchedPlans.map((plan: SubscriptionPlan) => {
        let originalPrice: number | undefined = undefined;
        let discountRate: number | undefined = undefined;

        // 정가 노출: 할인/원가/할인율 표기는 제거합니다.
        
        let description = '';
        let iconNode: React.ReactNode = undefined;

        if (plan.id === 'standard') {
          description = '막 시작한, 혹은 성장 중인 셀러를 위한 플랜';
          iconNode = <Zap className="w-6 h-6 text-blue-500" />;
        } else if (plan.id === 'premium') {
          description = '중대형 프로셀러를 위한 무제한 플랜';
          iconNode = <Sparkles className="w-6 h-6 text-purple-500" />;
        }

        return {
          id: plan.id,
          name: plan.name,
          price: plan.price,
          originalPrice,
          discountRate,
          limit: plan.usage_limit === null ? 'unlimited' : plan.usage_limit,
          copyLimit: plan.copy_usage_limit === null ? 'unlimited' : plan.copy_usage_limit,
          color: plan.id === 'standard' ? 'blue' : 'purple',
          recommended: plan.id === 'standard',
          features: getFeatures(plan.id, plan.copy_usage_limit),
          description: description,
          icon: iconNode,
        };
      });
      
      setPlans(formattedPlans);
    }
  }, [fetchedPlans]);

  // 사용량 통계 업데이트
  useEffect(() => {
    if (usageStat && subscriptionPlan) {
      setUsageStats({
        used: usageStat.used_count,
        total: subscriptionPlan.usage_limit === null ? 'unlimited' : subscriptionPlan.usage_limit,
        periodStart: new Date(usageStat.period_start),
        periodEnd: new Date(usageStat.period_end),
      });
      setCopyUsageStats({
        used: usageStat.copy_used_count,
        total: subscriptionPlan.copy_usage_limit === null ? 'unlimited' : subscriptionPlan.copy_usage_limit,
        periodStart: new Date(usageStat.period_start),
        periodEnd: new Date(usageStat.period_end)
      });
    }
  }, [usageStat, subscriptionPlan]);

  // 사용량 통계 실시간 갱신
  useEffect(() => {
    // 페이지 로드 시 최초 갱신
    if (user) {
      refreshSubscription();
    }
  }, [user, refreshSubscription]);

  // 피추천인 첫 결제 할인 여부 및 이벤트 여부 확인
  useEffect(() => {
    const checkReferralDiscount = async () => {
      if (!user) return;
      
      try {
        const { data: referralData } = await supabase
          .from('referral')
          .select('id, first_payment_at, event_id, is_event_period')
          .eq('referee_id', user.id)
          .single();
        
        // 피추천인이고 아직 첫 결제 전이면 할인 적용
        if (referralData && !referralData.first_payment_at) {
          setIsReferralDiscount(true);
          
          // 이벤트 조회 실패 = undefined → 혜택 문구 미표시 (기본 +1개월 오표시 방지)
          let referralEvent: ReferralEventState;
          try {
            referralEvent = await getActiveReferralEvent();
          } catch (eventError) {
            console.error('[MyPage] 이벤트 조회 오류:', eventError);
          }
          const label = refereeRewardLabel(referralData, referralEvent);
          setReferralRewardText(
            label.kind === 'event'
              ? `+ ${formatEventLastDay(referralEvent!.end_at)}까지 ${label.months}개월 추가 혜택${label.maxReferees ? ` (선착순 ${label.maxReferees}명)` : ''}`
              : label.kind === 'confirm'
                ? '+ 추가 혜택은 승인 시 확정됩니다'
                : label.kind === 'base'
                  ? '+ 첫 결제 시 1개월 추가 혜택'
                  : null
          );
        } else {
          setIsReferralDiscount(false);
          setReferralRewardText(null);
        }
      } catch {
        // 피추천인 아니거나 조회 실패 시 할인 미적용
        setIsReferralDiscount(false);
        setReferralRewardText(null);
      }
    };

    checkReferralDiscount();
  }, [user]);

  // 플랜별 기능 목록 반환
  const getFeatures = (planId: string, copyLimit: number | null): string[] => {
    const copyFeatureText = copyLimit === null ? '정산서 복사 기능 무제한' : `정산서 복사 기능 월 ${copyLimit}회`;
    switch (planId) {
      case 'standard':
        return [
          copyFeatureText,
          '배송 엑셀 자동 정리 기능 월 150회',
          '배송 엑셀 다운로드 기능 무제한',
          '입금매칭 기능 제공',
          '재고관리 기능 제공',
          '거파 고객 등록 기능 제공',
          '일별·월별 매출 기능 제공',
          '고객별 월 구매 총액 기능 제공'
        ];
      case 'premium':
        return [
          '스탠다드 플랜의 모든 기능',
          copyFeatureText,
          '배송 엑셀 자동 정리 기능 무제한',
          '단골 고객 순위 기능 제공',
          '많이 주문된 상품 월별 상위 10개 기능 제공'
        ];
      default:
        return [];
    }
  };
  
  // 카드 정보 변경 핸들러
  const handleChangeCard = async () => {
    if (!user) {
      toast.error('로그인이 필요합니다');
      return;
    }
    
    setCardLoading(true);
    try {
      // 1. 현재 세션에서 액세스 토큰 가져오기
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;

      if (!accessToken) {
        toast.error('인증 토큰을 가져올 수 없습니다');
        toast.error('인증 정보를 확인할 수 없습니다.');
        setCardLoading(false);
        return;
      }

      // 2. 새 빌링키 발급 요청
      try {
        // 새 빌링키 발급
        const billingKeyResponse = await issueBillingKey(user.id);

        if (!billingKeyResponse.billingKey) {
          setCardLoading(false);
          return;
        }

        // 새 카드가 동일한 카드인지 확인하기 위해 현재 카드 정보를 조회
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

        // 카드 등록 창 입력 동안 토큰이 만료될 수 있어 최신 토큰 재취득
        // (서버가 토큰 본인 검증을 하므로 stale 토큰이면 401로 기존 키 삭제가 누락됨)
        // 다른 탭에서 로그아웃/계정 전환된 경우(BroadcastChannel 전파) 진행 중단 —
        // 캡처해둔 옛 토큰으로 fallback하면 로그아웃 후 삭제, 타 계정 카드 삭제가 가능해진다
        const { data: freshSessionData } = await supabase.auth.getSession();
        const freshToken = freshSessionData?.session?.access_token;
        if (!freshToken || freshSessionData?.session?.user?.id !== user.id) {
          toast.error('로그인 상태가 변경되어 카드 변경을 중단했습니다.', {
            description: '다시 로그인한 뒤 시도해 주세요.',
          });
          setCardLoading(false);
          return;
        }

        // 기존 빌링키 조회
        const billingKeyCheckRes = await fetch(`${supabaseUrl}/functions/v1/billing-key`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${freshToken}`,
          },
          body: JSON.stringify({
            userId: user.id,
          }),
        });

        // 기존 빌링키가 있다면 삭제 진행
        if (billingKeyCheckRes.ok) {
          const existingCardInfo = await billingKeyCheckRes.json();

          // 기존 카드가 있는 경우, 삭제 처리
          if (existingCardInfo?.status === 'ISSUED') {
            // 기존 빌링키 삭제 (Edge Function 호출)
            const deleteRes = await fetch(`${supabaseUrl}/functions/v1/delete-billing-key`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${freshToken}`,
              },
              body: JSON.stringify({
                userId: user.id,
              }),
            });

            if (!deleteRes.ok) {
              let errorMsg = '';
              try {
                const errorData = await deleteRes.json();
                errorMsg = JSON.stringify(errorData);
              } catch {
                errorMsg = `HTTP 상태 코드: ${deleteRes.status}`;
              }

              toast.error('기존 빌링키 삭제 실패', {
                description: errorMsg
              });
              // 삭제 실패 시에도 계속 진행 - 새 빌링키는 이미 발급되었음
            }
          }
        }

        // 3. 새 빌링키 저장
        const paymentService = new PaymentService();
        await paymentService.registerBillingKey(user.id, billingKeyResponse.billingKey);

        // 4. 카드 정보 새로고침 트리거 (변경·신규 등록 공용 핸들러)
        toast.success('결제 카드가 등록되었습니다.');

        // 카드 정보 갱신 트리거 비동기 실행 (약간 지연)
        setTimeout(() => {
          refreshCardInfo();
        }, 1000);

        // 5. 연체 상태면 새 카드로 즉시 재결제 시도 (서버 클레임이 중복 결제 방어)
        // 청구 금액이 화면에 확인된 경우에만 자동 결제 — 금액 미확인 상태에서 돈이 나가지 않게
        if (isPastDue) {
          if (retryAmount != null) {
            toast.info(`새 카드로 재결제를 시작합니다. 결제 금액은 ${retryAmount.toLocaleString()}원입니다.`);
            await retryBilling();
          } else {
            toast.info('카드가 등록되었습니다. 결제 금액을 불러오지 못해 자동 재결제를 하지 않았습니다.', {
              description: '잠시 후 상단 배너의 재결제하기 버튼으로 결제해 주세요.',
            });
          }
        }
      } catch (error) {
        logError('카드 변경 중 오류 발생:', error);
        
        // 사용자가 결제창을 닫은 경우 에러 메시지 표시하지 않음
        if (isUserCancelError(error)) {
          console.log('사용자가 카드 등록을 취소했습니다');
        } else {
          toast.error('카드 변경 과정에서 오류가 발생했습니다', {
            description: error instanceof Error ? error.message : JSON.stringify(error)
          });
        }
      }
    } finally {
      setCardLoading(false);
    }
  };
  
  // 카드 로고 URL 결정 함수는 그대로 유지
  const getCardLogoUrl = (cardName: string, cardBrand?: string, cardIssuer?: string) => {
    // 카드사 로고 (국내 카드사 우선)
    if (cardIssuer?.toLowerCase().includes('samsung') || cardName.includes('삼성')) {
      return '/images/cards/samsung.svg';
    } else if (cardName.includes('현대')) {
      return '/images/cards/hyundai.svg';
    } else if (cardName.includes('신한')) {
      return '/images/cards/shinhan.svg';
    } else if (cardName.includes('KB') || cardName.includes('국민')) {
      return '/images/cards/kb.svg';
    } else if (cardName.includes('롯데')) {
      return '/images/cards/lotte.svg';
    } else if (cardName.includes('우리')) {
      return '/images/cards/woori.svg';
    } else if (cardName.includes('하나')) {
      return '/images/cards/hana.svg';
    } else if (cardName.includes('농협') || cardName.includes('NH')) {
      return '/images/cards/nh.svg';
    } else if (cardName.includes('BC') || cardName.includes('비씨')) {
      return '/images/cards/bc.svg';
    } 
    // 카드 브랜드 로고 (국내 카드사 정보가 없을 경우 VISA/마스터카드 표시)
    else if (cardBrand?.toLowerCase().includes('visa')) {
      return '/images/cards/visa.svg';
    } else if (cardBrand?.toLowerCase().includes('master')) {
      return '/images/cards/master.svg';
    }
    
    // 기본 카드 아이콘
    return '/images/cards/default-card.svg';
  };

  if (!user) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-8">
        <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
          <UserIcon className="h-16 w-16 text-gray-300 mb-4" />
          <h1 className="text-2xl font-bold mb-2">로그인이 필요합니다</h1>
          <p className="text-gray-500 mb-6">마이 페이지를 이용하려면 로그인이 필요합니다.</p>
        </div>
      </div>
    );
  }
  
  // 구독 관련 상태 확인
  const isPaidPlan = ['standard', 'premium'].includes(subscriptionPlan?.id || '');
  const isTrialPlan = subscription?.is_trial === true;
  const isPremiumTrial = isTrialPlan && subscriptionPlan?.id === 'premium';
  const isDowngradePending = subscription?.downgrade_to === 'standard';
  const isCancellationPending = subscription?.cancel_at_period_end === true;

  // 재결제 청구 금액 — 다운그레이드 예약 중 결제 실패면 대상 플랜 가격이 청구된다
  // (user-billing-retry의 planId = downgrade_to ?? subscription_plan_id 선택과 동일 기준)
  const retryAmount = subscription?.downgrade_to
    ? fetchedPlans.find((plan) => plan.id === subscription.downgrade_to)?.price ?? null
    : subscriptionPlan?.price ?? null;
  
  return (
    <div className="container max-w-7xl mx-auto py-6 space-y-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">마이 페이지</h1>
      
      {/* 결제 실패 알림 배너 */}
      {isPastDue && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4 rounded-md">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <Ban className="h-5 w-5 text-red-500" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">정기 결제 실패 알림</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>정기 결제가 실패하여 구독이 연체 상태입니다.</p>
                <p>재결제하기 버튼을 누르면 등록된 카드로 다시 결제합니다.</p>
                <p>결제가 계속 실패하면 아래에서 카드를 변경해 주세요.</p>
                <Button
                  onClick={retryBilling}
                  variant="destructive"
                  size="sm"
                  className="mt-2"
                  disabled={isRetrying || cardLoading || !user || retryAmount == null}
                >
                  {isRetrying ? "재결제 진행 중..." : "재결제하기"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 gap-8">
        {/* Basic Information Panel */}
        <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
          <div className="border-b px-6 py-4 flex items-center">
            <User className="h-5 w-5 mr-2 text-teal-500" />
            <h2 className="text-xl font-semibold text-gray-900">기본 정보</h2>
          </div>
          <div className="p-6">
            {isLoading ? (
              <UserProfileSkeleton />
            ) : (
              <div className="flex flex-col md:flex-row items-center gap-6">
                <Avatar className="w-24 h-24">
                  <AvatarImage src={user.user_metadata.avatar_url} />
                  <AvatarFallback className="text-xl bg-gray-100">{user.user_metadata.nickname?.[0]}</AvatarFallback>
                </Avatar>
                <div className="space-y-1">
                  <h3 className="text-2xl font-bold text-gray-900">{user.user_metadata.nickname}</h3>
                  <p className="text-gray-600">{user.email}</p>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* 기능 설정 섹션 (셀러별 opt-in) */}
        <FeatureSettingsSection />

        {/* 현업자 후기 섹션 - 구독 플랜 패널 위로 이동 */}
        <TestimonialsSection />

        {/* Subscription Plans Panel */}
        <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
          <div className="border-b px-6 py-4 flex items-center">
            <Settings className="h-5 w-5 mr-2 text-teal-500" />
            <h2 className="text-xl font-semibold text-gray-900">구독 플랜</h2>
          </div>
          <div className="p-6 space-y-6">
            {/* 현재 구독 상태 표시 */}
            {isLoading || plansLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-8 w-32" />
                <Skeleton className="h-4 w-full" />
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${ // Added font-medium for consistency
                    !subscriptionPlan || subscriptionPlan.id === 'free'
                      ? 'bg-gray-100 text-gray-800'
                      : subscriptionPlan.id === 'standard'
                        ? 'bg-teal-100 text-teal-800' // Standard uses teal
                        : 'bg-teal-100 text-teal-800' // Premium (paid or trial) uses teal
                  }`}>
                    {!subscriptionPlan
                      ? '플랜 정보 없음'
                      : subscriptionPlan.id === 'free'
                        ? '체험 기간 종료' // Changed from '플랜 미구독'
                        : isPremiumTrial
                          ? '프리미엄 체험 중'
                          : subscriptionPlan.id === 'standard'
                            ? '스탠다드 구독 중'
                            : '프리미엄 구독 중'}
                  </span>
                </div>
                <p className="text-gray-700">
                  {!subscriptionPlan
                    ? '플랜을 구독하면 더 많은 기능을 이용할 수 있어요.'
                    : subscriptionPlan.id === 'free'
                      ? '프리미엄 체험 기간이 종료되었습니다. 기능을 계속 이용하시려면 플랜을 구독해주세요.' // Changed message
                      : isPremiumTrial
                        ? `현재 프리미엄 체험판을 이용 중입니다.`
                        : `현재 ${subscriptionPlan.id === 'standard' ? '스탠다드' : '프리미엄'} 서비스를 이용 중입니다.`}
                </p>
              </div>
            )}
            
            {/* 구독 플랜 카드 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8 md:justify-center">
              {isLoading || plansLoading ? (
                <>
                  <PlanCardSkeleton />
                  <PlanCardSkeleton />
                </>
              ) : (
                plans.filter(plan => plan.id !== 'free').map(plan => (
                  <PlanCard
                    key={plan.id}
                    plan={plan}
                    currentPlan={subscriptionPlan?.id || 'standard'}
                    isReferralDiscount={isReferralDiscount}
                    referralRewardText={referralRewardText}
                  />
                ))
              )}
            </div>
          </div>
        </div>
        
        {/* Usage Panel */}
        <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
          <div className="border-b px-6 py-4 flex items-center">
            <BarChart className="h-5 w-5 mr-2 text-teal-500" />
            <h2 className="text-xl font-semibold text-gray-900">사용량</h2>
          </div>
          <div className="p-6">
            {isLoading ? (
              <UsageStatsSkeleton />
            ) : (
              <div className="space-y-8">
                {/* 자동 정리 기능 사용량 섹션 */}
                <div className="p-4 border rounded-lg bg-gray-50/50">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">배송 엑셀 자동 정리 기능 사용량</h3>
                  <UsageProgress value={usageStats.used} max={usageStats.total} label="자동 정리" />
                  <div className="mt-4 space-y-1 text-sm text-gray-600">
                    <div className="flex justify-between">
                      <span>월 사용 한도:</span>
                      <span className="font-medium">{usageStats.total === 'unlimited' ? '무제한' : `${usageStats.total}회`}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>남은 사용량:</span>
                      <span className="font-medium">{usageStats.total === 'unlimited' ? '무제한' : `${Math.max(0, (usageStats.total as number) - usageStats.used)}회`}</span>
                    </div>
                  </div>
                </div>

                {/* 정산서 복사 기능 사용량 섹션 */}
                <div className="p-4 border rounded-lg bg-gray-50/50">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">정산서 복사 기능 사용량</h3>
                  <UsageProgress value={copyUsageStats.used} max={copyUsageStats.total} label="정산서 복사" />
                  <div className="mt-4 space-y-1 text-sm text-gray-600">
                    <div className="flex justify-between">
                      <span>월 사용 한도:</span>
                      <span className="font-medium">{copyUsageStats.total === 'unlimited' ? '무제한' : `${copyUsageStats.total}회`}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>남은 사용량:</span>
                      <span className="font-medium">{copyUsageStats.total === 'unlimited' ? '무제한' : `${Math.max(0, (copyUsageStats.total as number) - copyUsageStats.used)}회`}</span>
                    </div>
                  </div>
                </div>
                
                {/* 다음 사용량 초기화 */}
                <div className="mt-6 pt-4 border-t border-gray-200 text-center">
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">다음 사용량 초기화:</span> {(() => {
                      const nextDate = new Date(usageStats.periodEnd); // Assuming both stats reset on the same day
                      nextDate.setDate(nextDate.getDate() + 1);
                      return nextDate.toLocaleDateString('ko-KR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      });
                    })()}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 카드 정보 패널 (유료 구독자만) */}
        {isPaidPlan && (
          <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
            <div className="border-b px-6 py-4 flex items-center">
              <CreditCard className="h-5 w-5 mr-2 text-teal-500" />
              <h2 className="text-xl font-semibold text-gray-900">등록된 결제 카드</h2>
            </div>
            <div className="p-6">
              {cardInfoLoading ? (
                <CardInfoSkeleton />
              ) : cardInfo ? (
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-5 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100">
                  <div className="flex flex-col md:flex-row items-center mb-4">
                    <div className="w-full md:w-64 h-40 flex items-center justify-center bg-gradient-to-br from-slate-50 to-white rounded-xl shadow-lg p-3 mb-4 md:mb-0 md:mr-6 relative overflow-hidden border-2">
                      <img 
                        src={getCardLogoUrl(cardInfo.cardName, cardInfo.cardBrand, cardInfo.cardIssuer)} 
                        alt={cardInfo.cardName} 
                        className="h-16 object-contain z-10"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/images/cards/default-card.svg';
                        }}
                      />
                      <div className="absolute bottom-3 left-0 right-0 px-4">
                        <div className="text-gray-600 font-mono text-sm text-center mt-2">{cardInfo.cardNumber}</div>
                      </div>
                    </div>
                    <div className="text-center md:text-left">
                      <div className="text-xl font-semibold text-gray-800 mb-2">{cardInfo.cardName}</div>
                      <div className="text-sm text-gray-500 mb-4">정기결제에 사용되는 카드입니다</div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full md:w-auto"
                        onClick={handleChangeCard}
                        disabled={cardLoading || isRetrying}
                      >
                        {cardLoading ? "처리 중..." : "카드 변경"}
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-xl">
                  <CreditCard className="h-8 w-8 text-gray-400 mb-2" />
                  <div className="text-gray-500 mb-3">등록된 카드 정보가 없습니다</div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleChangeCard}
                    disabled={cardLoading || isRetrying}
                  >
                    {cardLoading ? "처리 중..." : "카드 등록"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 구독 관리 패널 (유료 구독자만) */}
        {isPaidPlan && (
          <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
            <div className="border-b px-6 py-4 flex items-center">
              <Settings className="h-5 w-5 mr-2 text-teal-500" />
              <h2 className="text-xl font-semibold text-gray-900">구독 관리</h2>
            </div>
            <div className="p-6">
              <div className="flex flex-col md:flex-row items-start justify-between">
                <div className="mb-4 md:mb-0">
                  <div className="flex items-center">
                    <div className={`inline-flex items-center px-3 py-1 mr-3 rounded-full text-sm font-medium ${
                      subscriptionPlan?.id === 'standard'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {subscriptionPlan?.name || '구독 플랜'}
                    </div>
                    
                    {subscription?.is_trial && (
                      <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-50 text-purple-600 border border-purple-200">
                        체험판
                      </div>
                    )}
                    
                    {isDowngradePending && (
                      <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-600">
                        <ArrowDown className="h-3.5 w-3.5 mr-1" />
                        다운그레이드 예정
                      </div>
                    )}
                  </div>
                  <div className="mt-4 space-y-1">
                    <p className="text-gray-700">
                      {subscription?.is_trial ?
                        `현재 프리미엄 체험판을 이용 중입니다. 체험 기간 종료 후 서비스 이용이 제한될 수 있습니다.` : // Adjusted trial message
                        isDowngradePending ?
                          '다음 결제일부터 스탠다드 플랜으로 다운그레이드됩니다.' :
                          isCancellationPending ?
                            '구독이 취소되어 다음 결제일부터 서비스 이용이 제한됩니다.' : // Adjusted cancellation message
                            isPastDue ?
                              '결제 실패로 인해 구독 혜택이 제한됩니다. 상단의 재결제하기 버튼으로 다시 결제해 주세요.' :
                              `현재 ${subscriptionPlan?.name} 플랜을 이용 중입니다.`}
                    </p>
                    
                    {subscription?.is_trial ? (
                      <p className="text-sm text-purple-600 font-medium">
                        체험 종료일: {(() => {
                          const endDate = new Date(subscription.period_end);
                          endDate.setDate(endDate.getDate() + 1);
                          return endDate.toLocaleDateString('ko-KR', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          });
                        })()}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-500">
                        다음 결제일: {subscription ? (() => {
                          const nextBillingDate = new Date(subscription.period_end);
                          nextBillingDate.setDate(nextBillingDate.getDate() + 1);
                          return nextBillingDate.toLocaleDateString('ko-KR', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          });
                        })() : '-'}
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setShowSubscriptionDialog(true)}
                  disabled={isLoading}
                  className="min-w-[120px]"
                >
                  구독 관리
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* 구독 관리 다이얼로그 */}
      {user && subscription && (
        <SubscriptionManageDialog
          open={showSubscriptionDialog}
          onOpenChange={setShowSubscriptionDialog}
          userId={user.id}
          currentPlan={subscriptionPlan?.id || 'standard'}
          periodEnd={new Date(subscription.period_end)}
          hasDowngradePending={isDowngradePending}
          hasCancellationPending={isCancellationPending}
        />
      )}
    </div>
  );
} 